import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DbLead, getScoreColor, getScoreLabel, STATUS_OPTIONS, LeadStatus, getFitTier, FIT_TIER_DESCRIPTION } from "@/lib/types";
import { Flame, MapPin, Clock, CreditCard, Calendar, User, FileText, Loader2, ExternalLink, Sparkles, CheckCircle, XCircle, Radio, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useCalendar } from "@/hooks/useCalendar";
import { useCommunityBroadcasts } from "@/hooks/useCommunityBroadcasts";
import FitTierBadge from "@/components/FitTierBadge";
import BroadcastConfirmModal from "@/components/BroadcastConfirmModal";
import { RescheduleEventDialog, DeleteEventDialog } from "@/components/CalendarEventDialogs";
import { openExternalUrl, toLocalYMD } from "@/lib/validation";
import { DatePickerField } from "@/components/ui/date-picker-field";
import { TimePickerField } from "@/components/ui/time-picker-field";

interface Props {
  lead: DbLead | null;
  calendarProvider?: string | null;
  bookingLink?: string | null;
  onClose: () => void;
  onUpdate: (id: string, updates: Partial<DbLead>) => Promise<void>;
}

export default function LeadDrawer({ lead, calendarProvider, bookingLink, onClose, onUpdate }: Props) {
  const { createEvent, updateEvent, deleteEvent, creatingEvent } = useCalendar();
  const { createBroadcast, getBroadcastForLead } = useCommunityBroadcasts();
  const [eventDate, setEventDate] = useState<Date | undefined>();
  const [eventTime, setEventTime] = useState("10:00");
  const [eventResult, setEventResult] = useState<{ link: string; eventId: string | null } | null>(null);
  const [broadcasting, setBroadcasting] = useState(false);
  const [confirmBroadcastOpen, setConfirmBroadcastOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [deleteEventOpen, setDeleteEventOpen] = useState(false);

  if (!lead) return null;

  const existingBroadcast = getBroadcastForLead(lead.id);
  const fitTier = getFitTier(lead);
  const isLowFit = fitTier === "low_fit";

  const hasCalendar = calendarProvider && calendarProvider !== "none";
  const enrichment = lead.enrichment_data as any;

  async function handleStatusChange(status: string) {
    try {
      await onUpdate(lead!.id, { status: status as LeadStatus });
      toast.success(`Status updated to ${STATUS_OPTIONS.find(s => s.value === status)?.label}`);
    } catch {
      toast.error("Failed to update status");
    }
  }

  async function handleBookCall() {
    if (hasCalendar) {
      // Calendly generates a single-use scheduling link and ignores the
      // start time server-side — the lead picks their own slot on Calendly's
      // end, which is why no date/time picker is shown for that provider.
      let startTime: string;
      if (calendarProvider === "calendly") {
        startTime = new Date().toISOString();
      } else {
        if (!eventDate) {
          toast.error("Select a date for the call");
          return;
        }
        startTime = new Date(`${toLocalYMD(eventDate)}T${eventTime}:00`).toISOString();
      }
      const result = await createEvent(lead!.name, lead!.acuity_notes || "", startTime, 30, { leadId: lead!.id });
      if (result?.success) {
        if (calendarProvider === "calendly" && result.event_link) {
          setEventResult({ link: result.event_link, eventId: result.event_id ?? null });
          navigator.clipboard.writeText(result.event_link);
          toast.success("Link copied to clipboard — share it with the lead to book a time.");
        } else {
          toast.success("Event created!");
          if (result.event_link && result.event_id) {
            setEventResult({ link: result.event_link, eventId: result.event_id });
          }
        }
        if (calendarProvider === "google_calendar" && result.event_id) {
          await onUpdate(lead!.id, {
            google_event_id: result.event_id,
            google_event_link: result.event_link ?? null,
            calendar_event_start_at: startTime,
            calendar_event_created_at: new Date().toISOString(),
          });
        }
      }
    } else if (bookingLink) {
      openExternalUrl(bookingLink);
    } else {
      toast.info("Set up a calendar integration in Settings first.");
    }
  }

  const currentEventId = eventResult?.eventId ?? lead.google_event_id ?? null;
  const canManageEvent = currentEventId && (calendarProvider === "google_calendar" || calendarProvider === "zoom");

  async function handleReschedule(startTimeIso: string) {
    const result = await updateEvent(currentEventId!, lead!.name, lead!.acuity_notes || "", startTimeIso, 30, { leadId: lead!.id });
    if (result?.success) {
      toast.success("Event rescheduled!");
      await onUpdate(lead!.id, {
        calendar_event_start_at: startTimeIso,
        calendar_event_updated_at: new Date().toISOString(),
      });
      setRescheduleOpen(false);
    }
  }

  async function handleDeleteEvent() {
    const ok = await deleteEvent(currentEventId!, { leadId: lead!.id });
    if (ok) {
      toast.success("Event deleted");
      setEventResult(null);
      await onUpdate(lead!.id, {
        google_event_id: null,
        google_event_link: null,
        calendar_event_deleted_at: new Date().toISOString(),
      });
      setDeleteEventOpen(false);
    }
  }

  return (
    <Sheet open={!!lead} onOpenChange={open => { if (!open) { onClose(); setEventResult(null); setEventDate(""); } }}>
      <SheetContent className="overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            {lead.score === 100 && <Flame className="h-5 w-5 text-destructive animate-pulse" />}
            <span>{lead.name}</span>
            <span className={`flex items-center gap-1 text-sm font-bold ${getScoreColor(lead.score)}`}>
              <Flame className="h-4 w-4" />
              {lead.score} — {getScoreLabel(lead.score)}
            </span>
          </SheetTitle>
          {fitTier && (
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Fit for your agency:</span>
              <FitTierBadge tier={fitTier} size="md" />
            </div>
          )}
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {isLowFit && !existingBroadcast && (
            <div className="rounded-lg border border-warning/40 bg-warning/5 p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
              <div className="text-xs text-foreground/80">
                <p className="font-semibold text-foreground">This lead looks like a low fit for your agency.</p>
                <p className="mt-0.5">{FIT_TIER_DESCRIPTION.low_fit}</p>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="space-y-3">
            <InfoRow icon={MapPin} label="Zip Code" value={lead.zip_code || "—"} />
            <InfoRow icon={FileText} label="Care Needs" value={lead.acuity_notes || "—"} />
            <InfoRow icon={Clock} label="Urgency" value={`${lead.urgency ?? "—"}/5`} />
            <InfoRow icon={Clock} label="Hours Needed" value={lead.hours_needed ? `${lead.hours_needed} hrs/week` : "—"} />
            <InfoRow icon={CreditCard} label="Payer Source" value={lead.payer_source || "—"} />
            <InfoRow icon={User} label="Created" value={new Date(lead.created_at).toLocaleString()} />
          </div>

          {/* Enrichment Insights */}
          {lead.enriched && enrichment && (
            <div className="space-y-3 rounded-lg border p-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Enrichment Insights
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <EnrichmentChip label="High-Value Area" active={enrichment.high_value_area} />
                <EnrichmentChip label="Likely Private Pay" active={enrichment.likely_private_pay} />
                <EnrichmentChip label="Urgency Confirmed" active={enrichment.care_urgency_confirmed} />
                <EnrichmentChip
                  label={`Score Boost: +${enrichment.enrichment_score_boost || 0}`}
                  active={(enrichment.enrichment_score_boost || 0) > 0}
                />
              </div>
            </div>
          )}

          {/* Status */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Status</label>
            <Select value={lead.status} onValueChange={handleStatusChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Book Call */}
          <div className="space-y-3 rounded-lg border p-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Book Discovery Call
              {hasCalendar && (
                <span className="text-xs font-normal text-muted-foreground">
                  via {calendarProvider === "google_calendar" ? "Google Calendar" : calendarProvider === "calendly" ? "Calendly" : "Zoom"}
                </span>
              )}
            </h3>

            {(!hasCalendar || calendarProvider === "calendly" || !currentEventId) && (
              <>
                {hasCalendar && calendarProvider !== "calendly" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Date</Label>
                      <DatePickerField value={eventDate} onChange={setEventDate} placeholder="Select date" disabled={d => d < new Date(new Date().toDateString())} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Time</Label>
                      <TimePickerField value={eventTime} onChange={setEventTime} />
                    </div>
                  </div>
                )}

                <Button onClick={handleBookCall} className="w-full gap-2" disabled={creatingEvent}>
                  {creatingEvent ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Creating Event…</>
                  ) : (
                    <><Calendar className="h-4 w-4" /> {hasCalendar ? "Create Event" : "Book Discovery Call"}</>
                  )}
                </Button>
              </>
            )}

            {(() => {
              const persistedLink = lead.google_event_link;
              const persistedAt = lead.calendar_event_created_at;
              const link = eventResult?.link ?? persistedLink;
              if (!link) return null;
              return (
                <div className="space-y-1">
                  <a
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-sm text-primary underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open event link
                  </a>
                  {persistedAt && (
                    <p className="text-xs text-muted-foreground">
                      Event created {new Date(persistedAt).toLocaleString()}
                    </p>
                  )}
                  {lead.calendar_event_updated_at && (
                    <p className="text-xs text-muted-foreground">
                      Last rescheduled {new Date(lead.calendar_event_updated_at).toLocaleString()}
                    </p>
                  )}
                  {canManageEvent && (
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" onClick={() => setRescheduleOpen(true)}>
                        Reschedule
                      </Button>
                      <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setDeleteEventOpen(true)}>
                        Delete Event
                      </Button>
                    </div>
                  )}
                </div>
              );
            })()}
            {!eventResult?.link && !lead.google_event_link && lead.calendar_event_deleted_at && (
              <p className="text-xs text-muted-foreground">
                Previous event deleted {new Date(lead.calendar_event_deleted_at).toLocaleString()}
              </p>
            )}
          </div>

          {/* Broadcast to Community */}
          <div className="space-y-3 rounded-lg border p-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Radio className="h-4 w-4" />
              {isLowFit ? "Refer to Community" : "Broadcast to Community"}
            </h3>
            {existingBroadcast ? (
              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {existingBroadcast.status === "referred" ? "Referred" : "Broadcasting"}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  This lead is visible on the community board
                </span>
              </div>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  {isLowFit
                    ? "Pass this family to another agency that's a better fit. Their name stays private — you only share ZIP, care needs, payer, hours, and urgency."
                    : "Share this lead (without client name) with other agencies who may be able to serve them."}
                </p>
                <Button
                  variant={isLowFit ? "default" : "outline"}
                  className="w-full gap-2"
                  disabled={broadcasting}
                  onClick={() => setConfirmBroadcastOpen(true)}
                >
                  {broadcasting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radio className="h-4 w-4" />}
                  {isLowFit ? "Refer to Community" : "Broadcast to Community"}
                </Button>
              </>
            )}
          </div>

          {/* Transcript */}
          {lead.transcript && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground">Conversation Transcript</h3>
              <div className="rounded-lg bg-muted p-3 text-sm whitespace-pre-wrap text-foreground/80 max-h-64 overflow-y-auto">
                {lead.transcript}
              </div>
            </div>
          )}
        </div>

        <BroadcastConfirmModal
          lead={lead}
          open={confirmBroadcastOpen}
          onClose={() => setConfirmBroadcastOpen(false)}
          onConfirm={async () => {
            setBroadcasting(true);
            try {
              await createBroadcast(lead!);
              toast.success("Lead referred to the community!");
            } catch {
              toast.error("Failed to broadcast lead");
              throw new Error("broadcast_failed");
            } finally {
              setBroadcasting(false);
            }
          }}
        />

        <RescheduleEventDialog
          open={rescheduleOpen}
          onClose={() => setRescheduleOpen(false)}
          onConfirm={handleReschedule}
          loading={creatingEvent}
          initialStart={lead.calendar_event_start_at ? new Date(lead.calendar_event_start_at) : undefined}
        />
        <DeleteEventDialog
          open={deleteEventOpen}
          onClose={() => setDeleteEventOpen(false)}
          onConfirm={handleDeleteEvent}
          loading={creatingEvent}
        />
      </SheetContent>
    </Sheet>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      <span className="text-muted-foreground w-28">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

function EnrichmentChip({ label, active }: { label: string; active: boolean }) {
  return (
    <div className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium ${active ? "bg-green-100 text-green-800" : "bg-muted text-muted-foreground"}`}>
      {active ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {label}
    </div>
  );
}
